<template>
  <Transition name="connectivity-alert">
    <section
      v-if="shouldShowAlert"
      class="pointer-events-none fixed inset-x-0 top-3 z-[1200] flex justify-center px-3"
      :class="isAlertCollapsed ? 'connectivity-alert-shell-collapsed' : ''"
      role="alert"
      aria-live="assertive"
    >
      <div
        class="connectivity-alert-card app-glass pointer-events-auto flex w-fit max-w-[calc(100vw-1.5rem)] flex-col justify-center overflow-hidden rounded-lg border border-white/70 shadow-[0_1px_2px_rgb(0_0_0/0.04),0_8px_24px_-8px_rgb(0_0_0/0.15)] ring-1 ring-[var(--p-surface-200)] transition-all duration-200 dark:border-white/10 dark:ring-white/5 sm:max-w-3xl"
        :class="isAlertCollapsed ? 'connectivity-alert-card-collapsed' : ''"
        :tabindex="isAlertCollapsed ? 0 : undefined"
        @click="expandAlert"
        @keydown.enter="expandAlert"
        @keydown.space.prevent="expandAlert"
      >
        <div class="connectivity-alert-body flex items-center justify-center gap-2.5 py-2 pl-2.5 pr-3 text-sm">
          <span v-if="isAlertCollapsed" class="connectivity-alert-icons-collapsed flex shrink-0 items-center gap-1">
            <span
              v-for="(icon, index) in collapsedAlertIcons"
              :key="index"
              class="connectivity-alert-icon flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-500/10"
            >
              <component :is="icon" class="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
            </span>
          </span>

          <span
            v-else
            class="connectivity-alert-icon flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-500/10"
          >
            <component :is="alertIcon" class="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
          </span>

          <p
            class="connectivity-alert-content min-w-0 max-w-3xl whitespace-normal break-words text-center leading-5 text-[var(--p-text-color)] opacity-100 transition-all duration-200"
          >
            <span class="text-sm tracking-tight font-medium">{{ alertTitle }}</span>
            <br />
            <span class="ml-1 text-xs tracking-tight text-[var(--p-text-muted-color)]">
              {{ alertMessage }}
            </span>
            <span
              v-if="showRetryLabel"
              class="connectivity-alert-retry ml-1 inline-flex items-center text-xs tracking-tight"
            >
              {{ t('common.connectivity.alert.retry')
              }}<span class="connectivity-alert-dots" aria-hidden="true">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </span>
          </p>

          <button
            type="button"
            class="connectivity-alert-close cursor-pointer ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[var(--p-text-muted-color)] transition hover:bg-[var(--p-surface-100)] hover:text-[var(--p-text-color)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--p-primary-color)] dark:hover:bg-[var(--p-surface-800)]"
            :aria-label="t('general.close')"
            @click.stop="collapseAlert"
          >
            <X class="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        <div
          v-if="showRetryLabel && !isAlertCollapsed"
          class="h-0.5 w-full bg-[var(--p-surface-200)] dark:bg-[var(--p-surface-700)]"
        >
          <div class="connectivity-alert-progress h-full w-full origin-left bg-[var(--p-primary-color)]"></div>
        </div>
      </div>
    </section>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { BellOff, MicOff, WifiOff, X } from 'lucide-vue-next'

import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { useConnectivityStore } from '@/stores/useConnectivityStore'

const connectivityStore = useConnectivityStore()
const { t } = useI18n()
const { browserMicrophonePermission, checkMicrophonePermission, microphonePermission } =
  useWebphonePermissions()

const isMicrophoneDisconnected = computed(
  () => microphonePermission.value !== 'granted' && microphonePermission.value !== 'unknown',
)
const isMicrophoneUndetected = computed(() => microphonePermission.value === 'unavailable')
const notificationPermission = ref<NotificationPermission | 'unsupported' | 'unknown'>('unknown')
const isAlertCollapsed = ref(false)
const isMicrophonePermissionInitialized = ref(false)
let notificationPermissionStatusHandle: PermissionStatus | null = null
let notificationPermissionListener: (() => void) | null = null
let connectivityAlertDelayTimer: ReturnType<typeof setTimeout> | null = null
const CONNECTIVITY_ALERT_DELAY_MS = 4000
const isSipOnlyMicrophoneFailure = computed(
  () =>
    isMicrophoneDisconnected.value &&
    connectivityStore.networkOnline &&
    connectivityStore.webSocketConnected &&
    !connectivityStore.sipConnected,
)
const shouldShowMicrophoneAlert = computed(() => isMicrophoneDisconnected.value)
const shouldShowNotificationAlert = computed(
  () =>
    notificationPermission.value !== 'granted' &&
    notificationPermission.value !== 'unsupported' &&
    notificationPermission.value !== 'unknown',
)
const shouldShowRawConnectivityAlert = computed(
  () => connectivityStore.isAnyServiceDisconnected && !isSipOnlyMicrophoneFailure.value,
)
const shouldShowConnectivityAlert = ref(false)
const shouldUseMicrophoneAlert = computed(
  () => shouldShowMicrophoneAlert.value && !shouldShowConnectivityAlert.value,
)
const shouldUseNotificationAlert = computed(
  () => shouldShowNotificationAlert.value && !shouldShowConnectivityAlert.value && !shouldUseMicrophoneAlert.value,
)
const shouldShowAlert = computed(
  () => shouldShowConnectivityAlert.value || shouldUseMicrophoneAlert.value || shouldUseNotificationAlert.value,
)
const alertTitle = computed(() =>
  shouldUseMicrophoneAlert.value
    ? isMicrophoneUndetected.value
      ? t('common.connectivity.alert.microphone_undetected_title')
      : t('common.connectivity.alert.microphone_title')
    : shouldUseNotificationAlert.value
      ? t('common.connectivity.alert.notification_title')
      : t('common.connectivity.alert.title'),
)
const alertMessage = computed(() => {
  if (shouldUseMicrophoneAlert.value) {
    if (isMicrophoneUndetected.value) {
      return t('common.connectivity.messages.microphone_undetected')
    }

    return t('common.connectivity.messages.microphone')
  }

  if (shouldUseNotificationAlert.value) {
    return t('common.connectivity.messages.notification')
  }

  return connectivityStore.primaryMessage
})
const alertIcon = computed(() => {
  if (shouldUseMicrophoneAlert.value) return MicOff
  if (shouldUseNotificationAlert.value) return BellOff
  return WifiOff
})
const collapsedAlertIcons = computed(() => {
  const icons = []

  if (shouldShowConnectivityAlert.value) {
    icons.push(WifiOff)
  }

  if (shouldShowMicrophoneAlert.value) {
    icons.push(MicOff)
  }

  if (shouldShowNotificationAlert.value) {
    icons.push(BellOff)
  }

  return icons.length > 0 ? icons : [alertIcon.value]
})
const showRetryLabel = computed(() => shouldShowConnectivityAlert.value)

const syncNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    notificationPermission.value = 'unsupported'
    return
  }

  notificationPermission.value = Notification.permission
}

const attachNotificationPermissionListener = async () => {
  if (typeof window === 'undefined' || !('permissions' in navigator)) return

  try {
    notificationPermissionStatusHandle = await navigator.permissions.query({
      name: 'notifications' as PermissionName,
    })
    notificationPermission.value = notificationPermissionStatusHandle.state as NotificationPermission
    notificationPermissionListener = () => {
      notificationPermission.value = notificationPermissionStatusHandle?.state as NotificationPermission
    }
    notificationPermissionStatusHandle.addEventListener('change', notificationPermissionListener)
  } catch {
    syncNotificationPermission()
  }
}

const collapseAlert = () => {
  isAlertCollapsed.value = true
}

const expandAlert = () => {
  if (isAlertCollapsed.value) {
    isAlertCollapsed.value = false
  }
}

watch(shouldShowAlert, (isVisible) => {
  if (!isVisible) {
    isAlertCollapsed.value = false
  }
})

watch(
  shouldShowRawConnectivityAlert,
  (shouldShow) => {
    if (connectivityAlertDelayTimer) {
      clearTimeout(connectivityAlertDelayTimer)
      connectivityAlertDelayTimer = null
    }

    if (!shouldShow) {
      shouldShowConnectivityAlert.value = false
      return
    }

    connectivityAlertDelayTimer = setTimeout(() => {
      shouldShowConnectivityAlert.value = shouldShowRawConnectivityAlert.value
      connectivityAlertDelayTimer = null
    }, CONNECTIVITY_ALERT_DELAY_MS)
  },
  { immediate: true },
)

watch(browserMicrophonePermission, (currentPermission, previousPermission) => {
  if (
    !isMicrophonePermissionInitialized.value ||
    previousPermission === 'unknown' ||
    currentPermission === previousPermission
  ) {
    return
  }

  window.alert(t('common.connectivity.alert.microphone_permission_changed'))
  window.location.reload()
})

onMounted(() => {
  syncNotificationPermission()
  void attachNotificationPermissionListener()
  window.addEventListener('focus', syncNotificationPermission)
  document.addEventListener('visibilitychange', syncNotificationPermission)

  void checkMicrophonePermission().finally(() => {
    isMicrophonePermissionInitialized.value = true
  })
})

onUnmounted(() => {
  window.removeEventListener('focus', syncNotificationPermission)
  document.removeEventListener('visibilitychange', syncNotificationPermission)
  if (notificationPermissionListener) {
    notificationPermissionStatusHandle?.removeEventListener('change', notificationPermissionListener)
  }
  if (connectivityAlertDelayTimer) {
    clearTimeout(connectivityAlertDelayTimer)
  }
})
</script>

<style scoped>
.connectivity-alert-enter-active,
.connectivity-alert-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.connectivity-alert-enter-from,
.connectivity-alert-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

.connectivity-alert-card {
  animation: connectivity-alert-pop 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.connectivity-alert-shell-collapsed {
  top: 0;
}

.connectivity-alert-card-collapsed {
  height: 2.25rem;
  width: auto;
  min-width: 2.5rem;
  cursor: pointer;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.connectivity-alert-card-collapsed .connectivity-alert-body {
  gap: 0;
  padding: 0;
}

.connectivity-alert-card-collapsed .connectivity-alert-content {
  display: none;
  max-width: 0;
  opacity: 0;
}

.connectivity-alert-card-collapsed .connectivity-alert-close {
  display: none;
}

.connectivity-alert-icon {
  animation: connectivity-alert-icon-tilt 2.4s ease-in-out infinite;
}

.connectivity-alert-icons-collapsed {
  height: 2.25rem;
  padding-inline: 0.5rem;
}

.connectivity-alert-progress {
  animation: connectivity-alert-progress 1.8s ease-in-out infinite;
}

.connectivity-alert-retry {
  color: var(--p-text-muted-color);
  font-weight: 500;
}

.connectivity-alert-dots {
  display: inline-flex;
  justify-content: flex-start;
  width: 0.9rem;
  line-height: 1;
  vertical-align: bottom;
}

.connectivity-alert-dots span {
  animation: connectivity-alert-dot 1.4s ease-in-out infinite;
  opacity: 0.2;
}

.connectivity-alert-dots span:nth-child(2) {
  animation-delay: 160ms;
}

.connectivity-alert-dots span:nth-child(3) {
  animation-delay: 320ms;
}

@keyframes connectivity-alert-pop {
  0% {
    opacity: 0;
    transform: translateY(-0.4rem) scale(0.96);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes connectivity-alert-icon-tilt {
  0%,
  100% {
    transform: rotate(0deg);
  }

  50% {
    transform: rotate(-8deg);
  }
}

@keyframes connectivity-alert-progress {
  0% {
    transform: scaleX(0);
  }

  50% {
    transform: scaleX(0.7);
  }

  100% {
    transform: scaleX(1);
  }
}

@keyframes connectivity-alert-dot {
  0%,
  100% {
    opacity: 0.2;
  }

  35%,
  65% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .connectivity-alert-icon,
  .connectivity-alert-progress,
  .connectivity-alert-dots span {
    animation: none;
  }
}
</style>
